import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getMeApi, getSurveySummaryApi, uploadPhotoApi } from "../api/userApi";
import api from "../api/api";
import "./surveyDone.css";

export default function SurveyDone() {
  const navigate = useNavigate();
  const { slug } = useParams();

  const [user,       setUser]       = useState(null);
  const [selections, setSelections] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [uploading,  setUploading]  = useState(false);
  const [preview,    setPreview]    = useState(null);

  // Name editing
  const [editName,   setEditName]   = useState("");
  const [savingName, setSavingName] = useState(false);
  const [nameSaved,  setNameSaved]  = useState(false);

  const grouped = useMemo(() =>
    selections.map((s) => ({ step: s.step_no, label: s.label })),
  [selections]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        const [meRes, sumRes] = await Promise.all([
          getMeApi(),
          getSurveySummaryApi(slug),
        ]);
        if (!mounted) return;
        setUser(meRes.data.user);
        setEditName(meRes.data.user?.full_name || "");
        setSelections(sumRes.data.selections || []);

        // Mark survey done
        try {
          await fetch(
            `${process.env.REACT_APP_API_URL?.replace(/\/api$/, "") || "https://suvidha-backend-353j.onrender.com"}/api/users/survey-done`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          const cached = JSON.parse(localStorage.getItem("user") || "{}");
          localStorage.setItem("user", JSON.stringify({ ...cached, survey_done: true }));
        } catch { /* non-critical */ }

      } catch (err) {
        if (err?.response?.status === 401) navigate("/login");
        else alert(err?.response?.data?.message || "Failed to load summary");
      } finally {
        mounted && setLoading(false);
      }
    }
    load();
    return () => (mounted = false);
  }, [slug, navigate]);

  // ── Save name ─────────────────────────────────────────
  const handleSaveName = async () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === user?.full_name) return;
    setSavingName(true);
    try {
      await api.patch("/users/me", { full_name: trimmed });
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      stored.full_name = trimmed;
      localStorage.setItem("user", JSON.stringify(stored));
      setUser(u => ({ ...u, full_name: trimmed }));
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2500);
    } catch(e) {
      alert("Failed to update name. Please try again.");
    } finally {
      setSavingName(false);
    }
  };

  // ── Photo ─────────────────────────────────────────────
  const handlePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const fileInput = document.getElementById("photoInput");
    const file = fileInput?.files?.[0];
    if (!file) return alert("Please choose a photo first");
    try {
      setUploading(true);
      const res = await uploadPhotoApi(file);
      setUser(prev => ({ ...prev, profile_photo: res.data.profile_photo }));
      alert("Photo uploaded ✅");
    } catch (err) {
      alert(err?.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const backendBase = process.env.REACT_APP_API_URL?.replace(/\/api$/, "") || "https://suvidha-backend-353j.onrender.com";
  const photoUrl = user?.profile_photo ? `${backendBase}${user.profile_photo}` : null;

  if (loading) return <div className="done-page">Loading...</div>;

  return (
    <div className="done-page">
      <div className="done-card">
        <div className="done-header">
          <div>
            <h2 className="done-title">Survey Completed ✅</h2>
            <p className="done-subtitle">
              Thanks! Here's your profile & survey summary. We'll recommend the best plan for you.
            </p>
          </div>

          <div className="avatar-wrap">
            <div className="avatar">
              {preview ? (
                <img src={preview} alt="preview" />
              ) : photoUrl ? (
                <img src={photoUrl} alt="profile" />
              ) : (
                <div className="avatar-fallback">
                  {(user?.full_name?.[0] || "U").toUpperCase()}
                </div>
              )}
            </div>
            <form onSubmit={handleUpload} className="upload-box">
              <input id="photoInput" type="file" accept="image/*" onChange={handlePick} />
              <button className="btn-outline" type="submit" disabled={uploading}>
                {uploading ? "Uploading..." : "Upload Photo"}
              </button>
            </form>
          </div>
        </div>

        <div className="info-grid">

          {/* ── Editable Full Name ── */}
          <div className="info-item">
            <div className="info-label">Full Name</div>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <input
                type="text"
                value={editName}
                onChange={e => { setEditName(e.target.value); setNameSaved(false); }}
                onKeyDown={e => e.key === "Enter" && handleSaveName()}
                placeholder="Enter your full name"
                style={{
                  flex:1, padding:"7px 12px", borderRadius:8,
                  border:"1.5px solid #CBD5E1", fontSize:14,
                  fontFamily:"inherit", outline:"none", background:"#F8FAFC"
                }}
              />
              <button
                onClick={handleSaveName}
                disabled={savingName || !editName.trim() || editName.trim() === user?.full_name}
                style={{
                  padding:"7px 14px", borderRadius:8, border:"none",
                  background: nameSaved ? "#16A34A" : "#1F4E8C",
                  color:"#fff", fontWeight:700, fontSize:13,
                  cursor: (!editName.trim() || editName.trim() === user?.full_name) ? "not-allowed" : "pointer",
                  fontFamily:"inherit", whiteSpace:"nowrap",
                  opacity: (!editName.trim() || editName.trim() === user?.full_name) ? 0.5 : 1,
                  transition:"background .2s"
                }}
              >
                {savingName ? "..." : nameSaved ? "✓ Saved!" : "Save"}
              </button>
            </div>
          </div>

          <div className="info-item">
            <div className="info-label">Phone</div>
            <div className="info-value">{user?.phone || "-"}</div>
          </div>

          <div className="info-item">
            <div className="info-label">Email</div>
            <div className="info-value">{user?.email || "Not provided"}</div>
          </div>

          <div className="info-item">
            <div className="info-label">Survey</div>
            <div className="info-value">{slug}</div>
          </div>
        </div>

        <div className="summary">
          <h3 className="summary-title">Your Selections</h3>
          {grouped.length === 0 ? (
            <div className="empty">No selections found.</div>
          ) : (
            <div className="chips">
              {grouped.map((x, idx) => (
                <div className="chip" key={idx}>
                  <span className="chip-step">Step {x.step}</span>
                  <span className="chip-label">{x.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="done-actions">
          <button className="btn-secondary" onClick={() => navigate(`/survey/${slug}?stepNo=1`)}>
            Retake Survey
          </button>
          <button className="btn-primary" onClick={() => navigate("/plans")}>
            Go to Plans
          </button>
        </div>
      </div>
    </div>
  );
}